import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { api, authUrl, formatApiError } from "@/lib/api";
import { FONT_ROLES } from "@/lib/constants";

export default function BrandKit({ brand, onSaved }) {
  const kit = brand.kit || {};
  const [palette, setPalette] = useState(kit.palette || []);
  const [fonts, setFonts] = useState(
    kit.fonts?.length ? kit.fonts : FONT_ROLES.map((r) => ({ role: r, family: "" }))
  );
  const [voice, setVoice] = useState(kit.brand_voice || "");
  const [nc, setNc] = useState({ name: "", hex: "#000000" });

  const saveKit = async (payload) => {
    try {
      await api.put(`/brands/${brand.id}/kit`, payload);
      toast.success("Brand kit salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadLogo = async (variant, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", "logo");
    fd.append("category", variant);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/brands/${brand.id}/assets`, fd);
      await saveKit(variant === "light" ? { logo_light_id: data.id } : { logo_dark_id: data.id });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-12" data-testid="brand-kit-tab">
      {/* Logos */}
      <section>
        <p className="meta-label mb-4">LOGHI</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <LogoBox label="Versione chiara" bg="bg-white" assetId={kit.logo_light_id} onUpload={(f) => uploadLogo("light", f)} testid="logo-light" />
          <LogoBox label="Versione scura" bg="bg-black" assetId={kit.logo_dark_id} onUpload={(f) => uploadLogo("dark", f)} testid="logo-dark" />
        </div>
      </section>

      {/* Palette */}
      <section>
        <p className="meta-label mb-4">PALETTE COLORE</p>
        <div className="flex flex-wrap gap-4 mb-5">
          {palette.map((c, i) => (
            <div key={i} className="group relative">
              <div className="w-20 h-20 border border-black/20" style={{ backgroundColor: c.hex }} />
              <p className="font-mono2e text-[0.6rem] mt-1.5 text-ink">{c.hex?.toUpperCase()}</p>
              <p className="font-body text-xs text-stone2e truncate w-20">{c.name}</p>
              <button onClick={() => { const next = palette.filter((_, j) => j !== i); setPalette(next); saveKit({ palette: next }); }} data-testid={`delete-color-${i}`}
                className="absolute -top-2 -right-2 bg-black text-greige p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={11} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-4 bg-white/70 border border-white p-4 max-w-md">
          <input type="color" value={nc.hex} onChange={(e) => setNc({ ...nc, hex: e.target.value })} data-testid="color-picker" className="w-12 h-10 cursor-pointer border-0 bg-transparent" />
          <input value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder="Nome colore" data-testid="color-name-input" className="field-line" />
          <button onClick={() => { const next = [...palette, nc]; setPalette(next); saveKit({ palette: next }); setNc({ name: "", hex: "#000000" }); }} data-testid="add-color-button"
            className="bg-black text-greige px-4 py-2 shrink-0"><Plus size={14} strokeWidth={1.5} /></button>
        </div>
      </section>

      {/* Fonts */}
      <section>
        <p className="meta-label mb-4">FONT PER RUOLO TIPOGRAFICO</p>
        <div className="space-y-4 max-w-md">
          {fonts.map((f, i) => (
            <div key={f.role} className="flex items-center gap-4">
              <span className="meta-label w-28 shrink-0">{f.role?.toUpperCase()}</span>
              <input value={f.family} onChange={(e) => setFonts(fonts.map((x, j) => (j === i ? { ...x, family: e.target.value } : x)))}
                placeholder="es. Playfair Display" data-testid={`font-input-${i}`} className="field-line" />
            </div>
          ))}
          <button onClick={() => saveKit({ fonts })} data-testid="save-fonts-button" className="bg-black text-greige px-6 py-2 text-xs uppercase tracking-widest">Salva font</button>
        </div>
      </section>

      {/* Brand voice */}
      <section>
        <p className="meta-label mb-4">BRAND VOICE</p>
        <textarea rows={5} value={voice} onChange={(e) => setVoice(e.target.value)} placeholder="Come parla il brand: registro, parole da usare/evitare, esempi…"
          data-testid="brand-voice-input" className="field-line resize-none max-w-2xl" />
        <div className="mt-4">
          <button onClick={() => saveKit({ brand_voice: voice })} data-testid="save-voice-button" className="bg-black text-greige px-6 py-2 text-xs uppercase tracking-widest">Salva brand voice</button>
        </div>
      </section>
    </div>
  );
}

function LogoBox({ label, bg, assetId, onUpload, testid }) {
  return (
    <div>
      <div className={`${bg} border border-black/20 h-40 flex items-center justify-center overflow-hidden`}>
        {assetId ? (
          <img src={authUrl(`/assets/${assetId}/download`)} alt={label} className="max-h-32 max-w-[80%] object-contain" />
        ) : (
          <span className={`font-editorial italic ${bg === "bg-black" ? "text-greige/60" : "text-stone2e"}`}>Nessun logo</span>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="font-body text-xs text-stone2e">{label}</p>
        <label className="inline-flex items-center gap-1.5 text-xs text-ink cursor-pointer hover:text-stone2e" data-testid={`${testid}-upload`}>
          <Upload size={13} strokeWidth={1.5} /> Carica
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}
